import { useCallback, useEffect, useRef, useState } from "react";
import NetworkService from "../../NetworkService";

/**
 * @param {string | undefined} userId
 * @returns {{ data: object | null | undefined, loading: boolean, error: Error | null, refetch: () => Promise<object | null | undefined> }}
 */
function useUserById(userId) {
  const latestUserIdRef = useRef(userId);
  latestUserIdRef.current = userId;

  const [data, setData] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!userId || !NetworkService.jwtToken) {
      setData(undefined);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const requestForUserId = userId;

    setLoading(true);
    setError(null);

    try {
      const res = await NetworkService.getUserById(userId);
      const user = res?.[0] ?? null;
      if (requestForUserId !== latestUserIdRef.current) {
        return user;
      }
      setData(user);
      return user;
    } catch (err) {
      if (requestForUserId === latestUserIdRef.current) {
        setError(err);
        setData(null);
      }
      throw err;
    } finally {
      if (requestForUserId === latestUserIdRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    refetch().catch(() => {});
  }, [refetch, NetworkService.jwtToken]);

  return { data, loading, error, refetch };
}

export default useUserById;
